import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import BiButton from '@wix/da-bi/pkg/Components/BiButton';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import MoreMenuContent from './MoreMenuContent';
import ActionBarIcon from '../../_partials/ActionBarIcon';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import ActionBarToggleButton from '../../_partials/items/ActionBarToggleButton';
import MoreMenuMobileDropdown from './MoreMenuMobileDropdown';
import { getDeviationResourceType } from '../../../DeviationViews/_helpers';
import { NotificationType } from '../../../Notifications';
import { MoreMenuPopupAction, useActionsWithPopups } from './hooks';
import { BiData, MoreMenuClickBiEvent } from '@wix/da-bi/pkg/events';

import s from './MoreButton.scss';

export interface Props {
  deviation: PapiDeviation;
}

export const MoreButton: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const actionsWithPopups = useActionsWithPopups(deviation);

  if (isMobile) {
    return (
      <BiButton className={s['mobile-button']}>
        <ActionBarIcon icon={EllipsisIcon} />
        <MoreMenuMobileDropdown
          deviation={deviation}
          actionsWithPopovers={actionsWithPopups}
        />
      </BiButton>
    );
  }

  return (
    <>
      {Object.values(actionsWithPopups).map(
        popupAction => popupAction && popupAction.popup
      )}
      <ActionBarItemWrapper
        allowMobilePopover
        placement={isMobile ? 'top-end' : undefined}
        renderPopover={({ closeTooltip }) => (
          <MoreMenuContent
            deviation={deviation}
            closeTooltip={closeTooltip}
            actionsWithPopups={actionsWithPopups}
          />
        )}
        notificationTypes={
          (Object.values(actionsWithPopups) as MoreMenuPopupAction[])
            .map(
              (actionWithPopup): NotificationType | undefined =>
                actionWithPopup?.notificationType
            )
            .filter(
              notificationType => notificationType !== undefined
            ) as NotificationType[]
        }
        tooltip={t('actionbar.more.tooltip')}
        openBiData={BiData<MoreMenuClickBiEvent>({
          evid: 417,
          typeid: getDeviationResourceType(deviation),
          itemid: deviation.deviationId,
          sectionname: 'deviation',
        })}
      >
        {/* The onClick action is provided by popper, I believe. */}
        <ActionBarToggleButton
          icon={EllipsisIcon}
          aria-label={t('actionbar.more.tooltip')}
        />
      </ActionBarItemWrapper>
    </>
  );
};
MoreButton.displayName = 'MoreButton';

export default MoreButton;
